/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/jsx-props-no-spreading */
import { DragDropContext, DropResult, Droppable } from '@hello-pangea/dnd';
import { ButtonStyle } from 'common/button';
import { ClipActions, NotificationType, RecordingStatus } from 'common/enums';
import { ModalWarning } from 'common/modal-warning';
import { AppContext, appActions } from 'context/app-context';
import { classnames, hasVaultAccess, isAnyLoggedUser } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import React, { useContext, useEffect, useState } from 'react';
import { RouteName } from 'routes';
import { goToPage } from 'routes/route-helpers';

import { ClipItem } from './components/clip-item/clip-item';
import styles from './grouped-clips.module.scss';

type GroupedClipsProps = {
  vault: VaultType;
  clipSelected: ClipType;
  publicView?: boolean;
  notifyClipAction: (
    action: ClipActions,
    clip: ClipType,
    extraData?: ClipExtraDataType,
  ) => void;
  notifyStopClip?: () => void;
  notifyChangeClipOrder?: (clips: ClipType[]) => void;
};

const GroupedClips: React.FC<GroupedClipsProps> = ({
  clipSelected,
  publicView = false,
  vault,
  notifyClipAction,
  notifyStopClip,
  notifyChangeClipOrder,
}) => {
  const { state, dispatch } = useContext(AppContext);

  const getRecordedClips = (clipsList: ClipType[]) =>
    clipsList.filter(
      (clip: ClipType) => clip.recordingStatus === RecordingStatus.recorded,
    );

  const [clipsRecorded, setClipRecorded] = useState<ClipType[]>(
    getRecordedClips(vault.clips || []),
  );

  const [showModalSignUp, setShowModalSignUp] = useState(false);

  const { user } = state.data;

  const refetchVault = async () => {
    try {
      const response = await VaultsController.getVault(vault.id);
      if (!response || !response.clips) {
        return;
      }
      setClipRecorded(getRecordedClips(response.clips || []));
    } catch {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again later.',
          type: NotificationType.Error,
        }),
      });
    }
  };

  useEffect(() => {
    if (isAnyLoggedUser(user) && hasVaultAccess(user, vault)) {
      refetchVault();
    }
  }, [vault.clips]);

  const showModalSignUpComponent = () => (
    <ModalWarning
      title="Sign up to share or download your recordings"
      content={
        <div className={styles.modalSignUpInfo}>
          <div className="text__body__regular__medium__gray800">
            Please complete the sign up process to share or download your clips,
            as well as access the rest of our features.
          </div>
          <div className="text__body__regular__medium__gray800">
            Don&apos;t worry, your first vault is{' '}
            <span className="text__body__semi__bold__medium__gray800">
              free
            </span>{' '}
            for a month, and signing up only takes 2 minutes!
          </div>
        </div>
      }
      successButtonText="Sign up"
      closeFn={() => setShowModalSignUp(false)}
      successFn={() => goToPage(RouteName.SignUp)}
      successStyleButton={ButtonStyle.PrimaryFilled}
    />
  );

  const onDragEnd = async (result: DropResult) => {
    const { source, destination } = result;

    if (!destination || source.index === destination.index) {
      return;
    }

    const clips = Array.from(clipsRecorded);
    const [removed] = clips.splice(source.index, 1);
    clips.splice(destination.index, 0, removed);

    const allClipsList = clips.concat(vault.unansweredQuestions || []);

    const requestData: VaultEditRequestType = {
      vault: {
        questions: allClipsList,
      },
    };

    setClipRecorded(clips);

    try {
      const response = await VaultsController.updateVaultQuestions(
        vault.id,
        requestData,
      );
      if (!response || !response.clips) {
        return;
      }
      const clipsInOrder = getRecordedClips(response.clips);
      if (notifyChangeClipOrder) {
        notifyChangeClipOrder(clipsInOrder);
      }
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Changes saved',
          message: '',
          type: NotificationType.Success,
        }),
      });
      setClipRecorded(clipsInOrder);
    } catch (error) {
      if (vault.clips) {
        const clipsBeforeAction = getRecordedClips(vault.clips);
        setClipRecorded(clipsBeforeAction);
      }
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again later.',
          type: NotificationType.Error,
        }),
      });
    }
  };

  return (
    <>
      {showModalSignUp && showModalSignUpComponent()}

      <div className={styles.groupDateAndClips}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="clips-list" type="Clips">
            {(provided) => (
              <div
                className={classnames('text__body__regular__small__gray700')}
                ref={provided.innerRef}
              >
                {clipsRecorded.map((clip, index) => (
                  <ClipItem
                    key={clip.id}
                    clip={clip}
                    clipsRecorded={clipsRecorded}
                    selectedClip={clipSelected}
                    index={index}
                    vault={vault}
                    user={user}
                    publicView={publicView}
                    notifyClipAction={notifyClipAction}
                    notifyStopClip={notifyStopClip}
                    setShowModalSignUp={setShowModalSignUp}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </>
  );
};

export { GroupedClips };
